import * as Dfns from "date-fns/fp";
import * as UserRedux from "../../redux/user";

import {
  CommitSuccessDialog,
  DiscardConfirmationDialog,
  InstructionsDialog,
  ValidationErrorDialog,
} from "./OfflineInputsDialogs";
import {
  MenuItem,
  OfflineInputTabs,
  OfflineInputsTable,
  OutcomeDataTable,
  TimelineEntry,
} from "@blisspointmedia/bpm-types/dist/OfflineInputs";
import {
  NewTableEntriesMap,
  OrderByOptions,
  QueuedRequest,
  TableEditsMap,
  commitEntries,
  excelDownload,
  stageEntry,
} from "./OfflineInputsUtils";
import React, { useCallback, useEffect, useMemo, useState } from "react";

import AddPromoModal from "./AddPromoDataModal";
import { DateRange } from "../../utils/types";
import InputContainer from "./InputContainer";
import { SetError } from "../../redux/modals";
import TimelineBarChart from "./TimelineBarChart";
import { useSelector } from "react-redux";

interface PromoDataProps {
  inputType: keyof typeof OfflineInputTabs;
  originalTables: OutcomeDataTable[];
  promos: MenuItem[];
  setPromos: (promos: MenuItem[]) => void;
  offers: MenuItem[];
  setOffers: (offers: MenuItem[]) => void;
  timelineEntries: TimelineEntry[];
  stagedEntries: TimelineEntry[];
  setStagedEntries: React.Dispatch<React.SetStateAction<TimelineEntry[]>>;
  updated: { by?: string; at?: string };
  fetching: boolean;
  refetch: () => void;
  editingUnlocked: boolean;
  setEditingUnlocked: React.Dispatch<React.SetStateAction<boolean>>;
  dispatchStateReset: boolean;
  setDispatchStateReset: React.Dispatch<React.SetStateAction<boolean>>;
  company: string;
  setError: SetError;
}

const WIDTH_PER_MONTH = 800 / 12;

const Promos: React.FC<PromoDataProps> = ({
  inputType,
  originalTables,
  promos,
  setPromos,
  offers,
  setOffers,
  timelineEntries,
  stagedEntries,
  setStagedEntries,
  updated,
  fetching,
  refetch,
  editingUnlocked,
  setEditingUnlocked,
  dispatchStateReset,
  setDispatchStateReset,
  company,
  setError,
}) => {
  const [tables, setTables] = useState<OutcomeDataTable[]>([...originalTables]);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [, setTablesInEditMode] = useState<string[]>([]);
  const [, setNewTableEntries] = useState<NewTableEntriesMap>({});
  const [validationErrors, setValidationErrors] = useState<
    { tableId: string; errorDates: string[] }[]
  >([]);
  const [, setQueuedRequests] = useState<QueuedRequest[]>([]);
  const [, setDeletedTables] = useState<OutcomeDataTable[]>([]);
  const [showAddOutcomeDataModal, setShowAddOutcomeDataModal] = useState(false);
  const [showCommitSuccessDialog, setShowCommitSuccessDialog] = useState(false);
  const [showInstructionsDialog, setShowInstructionsDialog] = useState(false);
  const [showDiscardDialog, setShowDiscardDialog] = useState(false);
  const [tableEdits, setTableEdits] = useState<TableEditsMap>({});
  const [, setFilter] = useState<{ filter: (line: Record<string, any>) => boolean }>();
  const userFullName: string = useSelector(UserRedux.fullNameSelector) ?? "";
  const [orderBy, setOrderBy] = useState<OrderByOptions>(OrderByOptions.DATE_ASCENDING);
  const [timelineDateRange, setTimelineDateRange] = useState<DateRange>({
    start: Dfns.format("yyyy-MM-dd")(Dfns.sub({ months: 5 })(Dfns.startOfMonth(new Date()))),
    end: Dfns.format("yyyy-MM-dd")(Dfns.add({ months: 6 })(Dfns.endOfMonth(new Date()))),
  });

  // date ranges < 1 year will expand to size of container.
  // date ranges > 1 year will be scrollable, extending width based 800 px /year baseline
  const timelineChartWidth = useMemo(() => {
    const numDisplayedMonths =
      Dfns.differenceInCalendarMonths(Dfns.parseISO(timelineDateRange.start))(
        Dfns.parseISO(timelineDateRange.end)
      ) + 1;
    const additionalPixels =
      numDisplayedMonths > 12 ? (numDisplayedMonths - 12) * WIDTH_PER_MONTH : 0;
    return { width: `calc(100% + ${additionalPixels}px)` };
  }, [timelineDateRange]);

  const scrollToRow = (tableId, date) => {
    const idToFind = `${tableId}_${date.split("T")[0]}`;
    const foundEl = document.getElementById(idToFind);

    if (foundEl) {
      foundEl.scrollIntoView({ behavior: "smooth", block: "center" });
    } else {
      setTimeout(scrollToRow, 200, tableId, date);
    }
  };

  const resetState = useCallback(() => {
    setStagedEntries([]);
    setTables([...originalTables]);
    setSubmitting(false);
    setTablesInEditMode([]);
    setNewTableEntries({});
    setValidationErrors([]);
    setQueuedRequests([]);
    setDeletedTables([]);
    setShowAddOutcomeDataModal(false);
    setShowCommitSuccessDialog(false);
    setShowInstructionsDialog(false);
    setShowDiscardDialog(false);
    setTableEdits({});
    setFilter(undefined);
  }, [originalTables, setStagedEntries]);

  useEffect(() => {
    if (dispatchStateReset) {
      resetState();
      setDispatchStateReset(false);
    }
  }, [dispatchStateReset, resetState, setDispatchStateReset]);

  useEffect(() => {
    setTables([...originalTables]);
  }, [originalTables]);

  const onCommitSuccess = () => {
    refetch();
    resetState();
    setShowCommitSuccessDialog(true);
  };

  const validateAndUpdateTables = async () => {
    setSubmitting(true);
    await commitEntries(stagedEntries, onCommitSuccess, setError, company);
    setSubmitting(false);
  };

  const download = useCallback(() => excelDownload(inputType, tables), [inputType, tables]);

  return (
    <InputContainer
      dialogs={
        <>
          <AddPromoModal
            show={showAddOutcomeDataModal}
            handleClose={() => setShowAddOutcomeDataModal(false)}
            stageEntry={input => stageEntry(input, setStagedEntries)}
            promos={promos}
            setPromos={setPromos}
            offers={offers}
            setOffers={setOffers}
            userFullName={userFullName}
          />
          <CommitSuccessDialog
            show={showCommitSuccessDialog}
            onHide={() => setShowCommitSuccessDialog(false)}
          />
          <InstructionsDialog
            show={showInstructionsDialog}
            onHide={() => {
              setShowInstructionsDialog(false);
            }}
          />
          <DiscardConfirmationDialog
            show={showDiscardDialog}
            onDiscard={() => resetState()}
            onHide={() => setShowDiscardDialog(false)}
          />
          {validationErrors.length > 0 && (
            <ValidationErrorDialog
              errorDates={validationErrors[0].errorDates}
              onResolve={() => {
                validationErrors.shift();
                setValidationErrors([...validationErrors]);

                if (validationErrors.length === 0) {
                  validateAndUpdateTables();
                }
              }}
              onReject={() => {
                setValidationErrors([]);
                setShowInstructionsDialog(true);
                scrollToRow(validationErrors[0].tableId, validationErrors[0].errorDates[0]);
              }}
              table={tables.find(table => table.tableId === validationErrors[0].tableId)}
              tableEdits={tableEdits[validationErrors[0].tableId]}
              getInputDescription={function (table: OfflineInputsTable): string {
                const outcomeDataTable = table as OutcomeDataTable;
                return outcomeDataTable.kpiName;
              }}
              createEmptyEntry={date => {
                return {
                  date,
                  volume: undefined,
                };
              }}
              normalizeEntry={() => {}}
            />
          )}
        </>
      }
      tables={
        <div className="tablesContainerInner" style={timelineChartWidth}>
          <TimelineBarChart
            timelineEntries={[...timelineEntries, ...stagedEntries]}
            timelineDateRange={timelineDateRange}
            orderBy={orderBy}
            fieldPrettyName="Promos"
            chartContainerRightActions={<></>}
          />
        </div>
      }
      inputType={inputType}
      updated={updated}
      isEmpty={timelineEntries.length + stagedEntries.length === 0}
      fetching={fetching}
      submitting={submitting}
      editingUnlocked={editingUnlocked}
      setEditingUnlocked={setEditingUnlocked}
      onFilter={filter => {
        setFilter({ filter });
      }}
      validateAndUpdateTables={validateAndUpdateTables}
      setShowAddInputModal={setShowAddOutcomeDataModal}
      setShowDiscardDialog={setShowDiscardDialog}
      setError={setError}
      download={download}
      orderBy={orderBy}
      setOrderBy={setOrderBy}
      timelineDateRange={timelineDateRange}
      setTimelineDateRange={setTimelineDateRange}
    />
  );
};

export default Promos;
